import { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useI18nContext } from '@i18n/i18n-react';
import { EntityKey, EntityRelationType, EntityType } from '@common/api';
import { Button } from '@components/button';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { FlexGrow } from '@components/layout/Flex';
import { Delete, Edit, ChevronRightDouble, ChevronDownDouble } from '@components/icons/FarseerIcons';
import { useModal } from '@components/modal/ModalProvider';
import { PageLayout, PageSheet } from '@components/page/PageLayout';
import {
    Sheet,
    useSheetColumnHandlers,
    useSheetColumnConfigLocal,
    useSheetRowConfigLocal,
} from '@components/sheet/Sheet';
import { TooltipWrapper } from '@components/tooltip/Tooltip';

import { useEntity, useEntities, useCreateEntity, useIsEntityLoading } from '@data/entity/entity';
import { dimensionTagToEntityKey, tableTagToEntityKey } from '@data/entity/legacy';
import { useNewVariable } from '@data/entity/new';
import { useRelationTreeRows, useIsRelationLoading } from '@data/entity/relation';
import { EntityKeyRow } from '@data/entity/types';
import { isSamePseudoEntity, makeEntityKeySet, toRealEntityKey } from '@data/entity/util';
import {
    EntityColumnType,
    useEntityTableExpand,
    useEntityTableVisibleKeys,
    useEntityTableSubscribe,
    useEntityTableColumns,
    useEntityTableMutation,
    useEntityTableCanExpandCollapse,
    useEntityTableRowReorder,
} from '@features/entity/entityTable';
import { useFolderEntityKey, getFolderKeyPath, useFolderNavigateOnDelete } from '@features/entity/entityFolder';
import { EntityRename } from '@features/entity/EntityRename';
import { DeleteTagsModal } from '@features/finder/modals/DeleteTagsModal';
import { DeleteTagDataModal } from '@features/finder/modals/DeleteTagDataModal';
import { useTagModalSelect } from '@features/tags/tagModalSelect';
import { VariableStatsModal } from '@features/variable-stats/VariableStats';
import { PAGE_ICONS } from '@util/folderItems';
import { pagePath } from '@pages/pagePaths';
import { FolderBreadcrumbs } from '@components/folder-breadcrumbs/FolderBreadcrumbs';
import { ShareSidebarToggle } from '@features/sidebar/ShareSidebarToggle';
import { SidebarType } from '@util/enums';
import { ShareSidebar } from '@features/share-editor/ShareSidebar';
import { useDerivedState } from '@hooks/useDerivedState';

export const SELECTED_VARIABLE_KEY = 'selected_variable';

const SHEET_STYLE = {
    freezeColumns: 1,
};

const ENTITY_COLUMN_TYPES = [
    { type: EntityColumnType.MetaName },
    { type: EntityColumnType.MetaDescription },
    { type: EntityColumnType.CalculationFormula },
    { type: EntityColumnType.VariableNotes },
    { type: EntityColumnType.VariableRollup },
    { type: EntityColumnType.VariableReadOnly },
    { type: EntityColumnType.VariableDimensions },
    { type: EntityColumnType.VariableOnDemand },
];

export const useVisibleVariableDimensionKeys = (visibleKeys: EntityKey[]) => {
    const entities = useEntities(visibleKeys);

    return useMemo(() => {
        const keySet = makeEntityKeySet<EntityKey>(visibleKeys);

        for (const entity of entities) {
            if (!entity) continue;

            if (entity.variable) {
                for (const gid of entity.variable.tableGids) {
                    keySet.push(dimensionTagToEntityKey(gid));
                }
                for (const gid of entity.variable.memberGids) {
                    keySet.push(tableTagToEntityKey(gid));
                }
            }
        }
        return keySet.all();
    }, [visibleKeys, entities]);
};

export const VariableEditor: FC = () => {
    const { LL } = useI18nContext();
    const { variableId } = useParams();
    const { showModal } = useModal();
    const handleTagSelect = useTagModalSelect();

    // Load folder
    const folderKey = useFolderEntityKey();
    const folder = useEntity(folderKey);
    const canEdit = !!folder?.permissions?.canEdit;
    useFolderNavigateOnDelete(folderKey);

    // Set up expand state
    const folderPath = getFolderKeyPath(folder);
    const expandClusters = useEntityTableExpand(folderPath);
    const canExpand = useCallback((e: EntityKey) => e.type === EntityType.Folder, []);

    // Fetch relation tree as flat rows
    // This is just the entity keys, not the entities
    const treeRows = useRelationTreeRows(folderKey, EntityRelationType.Folder, canExpand, expandClusters.isExpanded);
    const { canExpandAll, canCollapseAll } = useEntityTableCanExpandCollapse(treeRows, expandClusters);
    const rowCount = treeRows.length;

    const [selectedKey, setSelectedKey] = useDerivedState<EntityKey | null>(
        () => (variableId ? { type: EntityType.Variable, id: Number(variableId) } : null),
        [variableId]
    );
    const selectedRowIndex = treeRows.findIndex((row) => isSamePseudoEntity(row.key, selectedKey));
    const selectedEntity = useEntity(selectedKey);

    const handleSelectedRowChange = (rowIndex: number | null) => {
        const row = rowIndex != null ? treeRows[rowIndex] : null;
        setSelectedKey(toRealEntityKey(row?.key) ?? null);
    };

    // We lazy-join each row with the entity store in the sheet,
    // so we subscribe to all visible rows on the fly.
    const { handleScrollChange, visibleKeys } = useEntityTableVisibleKeys(treeRows);
    const { handleRowOrderChange } = useEntityTableRowReorder(treeRows, EntityRelationType.Folder);
    const allKeys = useVisibleVariableDimensionKeys(visibleKeys);
    useEntityTableSubscribe(allKeys);

    // New variable
    const getNewVariable = useNewVariable(folderKey);
    const createEntity = useCreateEntity();
    const handleNewVariable = async (name?: string) => {
        await createEntity(getNewVariable(name));
    };

    // Prepare static column handlers
    const columns = useEntityTableColumns(ENTITY_COLUMN_TYPES, expandClusters, rowCount, canEdit, getNewVariable);

    // Local column reordering
    const { orderedColumns, setColumnConfig } = useSheetColumnConfigLocal(columns, 'variable-editor');
    const columnHandlers = useSheetColumnHandlers(orderedColumns);

    // Local row resizing
    const { rowConfig, setRowConfig } = useSheetRowConfigLocal('variable-editor');

    // Mutations
    const handleEntityMutation = useEntityTableMutation();

    // Page
    const rowKey = (row: EntityKeyRow) => row.path.join('.');
    const disabled = !selectedEntity?.permissions?.canEdit;
    const disabledIfNotVariable = disabled || selectedKey?.type !== EntityType.Variable;

    const title = <FlexGrow>{folderPath.length > 1 ? <EntityRename entity={folder} /> : folder?.meta.name}</FlexGrow>;

    // Loading
    // (use + as unconditional or)
    const isFolderLoading = useIsEntityLoading(folderKey);
    const isRelationLoading = useIsRelationLoading(folderKey, EntityRelationType.Folder);
    const loading = !!(isFolderLoading || isRelationLoading);

    const folderBreadcrumbs = <FolderBreadcrumbs entityKey={folderKey} pathType={pagePath.Variables} />;

    const actions = (
        <>
            <TooltipWrapper label={LL.COLLAPSE_ALL()} allowedPlacements={['bottom']}>
                <Button
                    $type="quaternary"
                    icon={<ChevronRightDouble />}
                    disabled={!canCollapseAll}
                    onClick={expandClusters.collapseAll}
                />
            </TooltipWrapper>
            <TooltipWrapper label={LL.EXPAND_MORE()} allowedPlacements={['bottom']}>
                <Button
                    $type="quaternary"
                    icon={<ChevronDownDouble />}
                    disabled={!canExpandAll}
                    onClick={expandClusters.expandAll}
                />
            </TooltipWrapper>
            <Button
                $type="secondary"
                $color="secondary"
                title={LL.VARIABLE_METRICS()}
                style={{ marginLeft: 10, whiteSpace: 'nowrap' }}
                loading={loading}
                onClick={() => showModal(<VariableStatsModal />)}
            />
            <Button
                $type="primary"
                $color="secondary"
                title={LL.NEW_VARIABLE()}
                style={{ marginLeft: 10, whiteSpace: 'nowrap' }}
                disabled={false}
                loading={loading}
                onClick={() => handleNewVariable()}
            />
        </>
    );

    return (
        <PageLayout
            toggles={<ShareSidebarToggle />}
            title={title}
            actions={actions}
            sidebars={{ [SidebarType.SHARE]: <ShareSidebar /> }}
            folderBreadcrumbs={folderBreadcrumbs}
            icon={PAGE_ICONS.Variables}
            padded
        >
            <PageSheet>
                <Sheet
                    rows={treeRows}
                    rowKey={rowKey}
                    rowConfig={rowConfig}
                    columnHandlers={columnHandlers}
                    onColumnConfig={setColumnConfig}
                    onRowConfig={setRowConfig}
                    contextMenu={(props) => (
                        <ContextMenu {...props}>
                            <ContextMenu.Item
                                item={{
                                    icon: Edit,
                                    label: LL.EDIT_VARIABLE(),
                                    disabled: disabledIfNotVariable,
                                    action: () => {
                                        if (selectedKey) handleTagSelect(selectedKey);
                                    },
                                }}
                            />
                            <ContextMenu.Divider />
                            <ContextMenu.Item
                                item={{
                                    icon: Delete,
                                    label: LL.DELETE(),
                                    disabled,
                                }}
                            >
                                <ContextMenu.Item
                                    item={{
                                        label: LL.DELETE_VARIABLE_DATA(),
                                        disabled: disabledIfNotVariable,
                                        action: () => {
                                            if (selectedKey) showModal(<DeleteTagDataModal tagKey={selectedKey} />);
                                        },
                                    }}
                                />
                                <ContextMenu.Item
                                    item={{
                                        label: LL.DELETE_VARIABLE(),
                                        disabled: disabledIfNotVariable,
                                        action: () => {
                                            if (selectedKey) showModal(<DeleteTagsModal tagKeys={[selectedKey]} />);
                                        },
                                    }}
                                />
                            </ContextMenu.Item>
                        </ContextMenu>
                    )}
                    onRowOrderChange={handleRowOrderChange}
                    onSelectedRowChange={handleSelectedRowChange}
                    selectedRowIndexes={[selectedRowIndex]}
                    onScrollChange={handleScrollChange}
                    onUpdate={handleEntityMutation}
                    sheetStyle={SHEET_STYLE}
                />
            </PageSheet>
        </PageLayout>
    );
};
